import { useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { decodeJWTPayload } from '../utils/jwt';
import { useState, useEffect } from 'react';

export interface Permissions {
  // Loading state - true while permissions are being resolved
  isLoading: boolean;
  
  // System-level permissions
  isAdmin: boolean;
  isDeveloper: boolean;
  
  // Company-level role
  role: string | null;
  
  // Feature permissions
  canManageSubscriptions: boolean; // Create/edit subscription prices
  canManageDiscountCodes: boolean; // Create/edit discount codes
  canManageBilling: boolean; // Stripe billing setup
  canEditGymDetails: boolean; // Edit company details
  canViewAccountRecords: boolean; // View account manager
  canInviteToSubscriptions: boolean; // Invite users to subscriptions
  canApproveAttendance: boolean; // Approve attendance requests
  canEnterAccountNotes: boolean; // Enter account notes
  canClockIn: boolean; // Clock in/out
  canViewOtherStaffPunches: boolean; // View punches from other staff
  canUpdateClassRegistrations: boolean; // Update class registrations
  canAppearInCoachDropdown: boolean; // Appear in coach dropdown for classes
  canManagePrivateLessons: boolean; // Private lesson capabilities (future)
  canAccessStripeConfig: boolean; // Access Stripe configuration (admin only)
}

/**
 * Comprehensive permission hook that checks user's role and system permissions
 * Uses JWT claims directly (matches RLS policies) - no database queries needed
 * Returns detailed permission flags based on role hierarchy
 */
export function usePermissions(): Permissions {
  const [role, setRole] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          return;
        }

        // PRIMARY: Read all permissions from JWT claims (set by custom_access_token_hook)
        // This matches what RLS policies use and is the source of truth
        const claims = session.access_token ? decodeJWTPayload(session.access_token) : null;
        
        if (claims) {
          // Read role from JWT (matches RLS get_jwt_user_role())
          const jwtRole = claims.user_role || null;
          setRole(jwtRole);
          
          // Read elevated permissions from JWT
          setIsAdmin(claims.is_admin === true);
          setIsDeveloper(claims.is_developer === true);
        } else {
          // FALLBACK: If JWT decode fails, check database (shouldn't happen normally)
          // This handles edge cases where JWT hasn't been refreshed yet
          setRole(null);
          
          // Check admin/developer from database as fallback
          const { data: adminCheck } = await supabase
            .from('admins')
            .select('user_id')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (adminCheck) {
            setIsAdmin(true);
          } else {
            const { data: devCheck } = await supabase
              .from('developer_profiles')
              .select('id, is_active')
              .eq('id', session.user.id)
              .maybeSingle();
            
            if (devCheck?.is_active) {
              setIsDeveloper(true);
            }
          }
        }
      } catch (err) {
        console.error('[usePermissions] Error checking permissions:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, []);

  const permissions = useMemo((): Permissions => {
    // Admin: All permissions
    if (isAdmin) {
      return {
        isLoading,
        isAdmin: true,
        isDeveloper: false,
        role: 'admin',
        canManageSubscriptions: true,
        canManageDiscountCodes: true,
        canManageBilling: true,
        canEditGymDetails: true,
        canViewAccountRecords: true,
        canInviteToSubscriptions: true,
        canApproveAttendance: true,
        canEnterAccountNotes: true,
        canClockIn: true,
        canViewOtherStaffPunches: true,
        canUpdateClassRegistrations: true,
        canAppearInCoachDropdown: true,
        canManagePrivateLessons: true,
        canAccessStripeConfig: true,
      };
    }

    // Developer: All admin permissions except Stripe config
    if (isDeveloper) {
      return {
        isLoading,
        isAdmin: false,
        isDeveloper: true,
        role: 'developer',
        canManageSubscriptions: true,
        canManageDiscountCodes: true,
        canManageBilling: false, // No Stripe config access
        canEditGymDetails: true,
        canViewAccountRecords: true,
        canInviteToSubscriptions: true,
        canApproveAttendance: true,
        canEnterAccountNotes: true,
        canClockIn: true,
        canViewOtherStaffPunches: true,
        canUpdateClassRegistrations: true,
        canAppearInCoachDropdown: true,
        canManagePrivateLessons: true,
        canAccessStripeConfig: false, // Devs cannot access Stripe config
      };
    }

    // Owner: All gym permissions
    if (role === 'owner') {
      return {
        isLoading,
        isAdmin: false,
        isDeveloper: false,
        role: 'owner',
        canManageSubscriptions: true,
        canManageDiscountCodes: true,
        canManageBilling: true,
        canEditGymDetails: true,
        canViewAccountRecords: true,
        canInviteToSubscriptions: true,
        canApproveAttendance: true,
        canEnterAccountNotes: true,
        canClockIn: true,
        canViewOtherStaffPunches: true,
        canUpdateClassRegistrations: true,
        canAppearInCoachDropdown: true,
        canManagePrivateLessons: true,
        canAccessStripeConfig: false,
      };
    }

    // Gym Admin: All owner permissions except subscription prices, stripe billing, gym details editing
    if (role === 'admin') {
      return {
        isLoading,
        isAdmin: false,
        isDeveloper: false,
        role: 'admin',
        canManageSubscriptions: false, // Cannot manage subscription prices
        canManageDiscountCodes: true,
        canManageBilling: false, // Cannot manage Stripe billing setup
        canEditGymDetails: false, // Cannot edit gym details
        canViewAccountRecords: true,
        canInviteToSubscriptions: true,
        canApproveAttendance: true,
        canEnterAccountNotes: true,
        canClockIn: true,
        canViewOtherStaffPunches: true, // Can view punches from other staff
        canUpdateClassRegistrations: true,
        canAppearInCoachDropdown: true,
        canManagePrivateLessons: true,
        canAccessStripeConfig: false,
      };
    }

    // Coach: View account records, invite to subscriptions, approve attendance, enter notes, clock in, update class registrations
    // NO subscription creation or discount codes (same as admin)
    if (role === 'coach') {
      return {
        isLoading,
        isAdmin: false,
        isDeveloper: false,
        role: 'coach',
        canManageSubscriptions: false, // Cannot create/edit subscriptions
        canManageDiscountCodes: false, // Cannot create/edit discount codes
        canManageBilling: false,
        canEditGymDetails: false,
        canViewAccountRecords: true,
        canInviteToSubscriptions: true,
        canApproveAttendance: true,
        canEnterAccountNotes: true,
        canClockIn: true,
        canViewOtherStaffPunches: false,
        canUpdateClassRegistrations: true,
        canAppearInCoachDropdown: true, // Appears in coach dropdown
        canManagePrivateLessons: true,
        canAccessStripeConfig: false,
      };
    }

    // Reception: Same as coach except do not appear in coach dropdown and no private lesson capabilities
    if (role === 'reception') {
      return {
        isLoading,
        isAdmin: false,
        isDeveloper: false,
        role: 'reception',
        canManageSubscriptions: false,
        canManageDiscountCodes: false,
        canManageBilling: false,
        canEditGymDetails: false,
        canViewAccountRecords: true,
        canInviteToSubscriptions: true,
        canApproveAttendance: true,
        canEnterAccountNotes: true,
        canClockIn: true,
        canViewOtherStaffPunches: false,
        canUpdateClassRegistrations: true,
        canAppearInCoachDropdown: false, // Do NOT appear in coach dropdown
        canManagePrivateLessons: false, // No private lesson capabilities
        canAccessStripeConfig: false,
      };
    }

    // Default: No permissions (student or unknown role)
    return {
      isLoading,
      isAdmin: false,
      isDeveloper: false,
      role: role || null,
      canManageSubscriptions: false,
      canManageDiscountCodes: false,
      canManageBilling: false,
      canEditGymDetails: false,
      canViewAccountRecords: false,
      canInviteToSubscriptions: false,
      canApproveAttendance: false,
      canEnterAccountNotes: false,
      canClockIn: false,
      canViewOtherStaffPunches: false,
      canUpdateClassRegistrations: false,
      canAppearInCoachDropdown: false,
      canManagePrivateLessons: false,
      canAccessStripeConfig: false,
    };
  }, [isAdmin, isDeveloper, role, isLoading]);

  return permissions;
}

/**
 * Hook to check if user has any of the specified roles
 * Uses JWT claims directly (matches RLS policies)
 */
export function useHasRole(allowedRoles: string[]): boolean {
  const permissions = usePermissions();
  
  // Admins and developers have access to everything
  if (permissions.isAdmin || permissions.isDeveloper) {
    return true;
  }
  
  if (!permissions.role) {
    return false;
  }
  
  return allowedRoles.includes(permissions.role);
}

/**
 * Hook to check if user has a specific permission
 */
export function useHasPermission(permission: keyof Omit<Permissions, 'isLoading' | 'isAdmin' | 'isDeveloper' | 'role'>): boolean {
  const permissions = usePermissions();
  return permissions[permission];
}
